import '@operato/dataset/ox-data-entry-form.js'
import '@operato/dataset/ox-checklist-sample-view.js'

import gql from 'graphql-tag'
import { css, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { i18next, localize } from '@operato/i18n'
import { ScrollbarStyles } from '@operato/styles'
import { client } from '@operato/graphql'

@customElement('activity-data-review-view')
export class DataReviewActivityView extends localize(i18next)(LitElement) {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        background-color: var(--md-sys-color-surface);
      }

      div[content] {
        flex: 1;
        flex-direction: column;

        display: flex;
        overflow: auto;
      }

      ox-data-sample-view,
      ox-checklist-sample-view {
        flex: 1;
        padding: var(--spacing-large);
        overflow: auto;
      }

      div[role='radiogroup'] {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-medium);

        label {
          display: flex;
          flex-direction: row;

          gap: var(--spacing-small);
          align-items: center;
        }
      }

      label {
        display: flex;
        flex-direction: column;

        padding: var(--spacing-large);

        h2 {
          display: flex;

          color: var(--title-text-color);
          text-transform: capitalize;
          margin: var(--title-margin);
          align-items: center;
        }

        div {
          padding: var(--spacing-large);
          font: var(--input-field-font);
          border: var(--md-sys-color-outline) solid 1px;
          border-radius: var(--spacing-small);
          background-color: var(--md-sys-color-surface-variant);
        }

        div[instruction] {
          min-height: 200px;
        }

        md-icon {
          color: var(--status-danger-color);
        }
      }

      @media print {
        :host {
          display: block;
          overflow: visible;
        }

        div[content] {
          display: block;
          overflow: visible;
        }
      }
    `
  ]

  @property({ type: Object }) input?: any
  @property({ type: Object }) output?: any

  @state() dataSample?: any

  render() {
    const { judgment, instruction } = this.output || {}

    return html`
      <div content>${this.showDataSampleView()}</div>

      <form>
        <label>
          <h2><md-icon>build_circle</md-icon>&nbsp;<span>${i18next.t('label.judgment')}</span></h2>
          <div role="radiogroup" aria-label="An example group of radio buttons">
            <label>
              <md-radio
                name="judgment"
                value="normal"
                ?checked=${judgment == 'normal'}
                aria-label=${i18next.t('label.judgment-normal')}
                disabled
              ></md-radio>
              ${i18next.t('label.judgment-normal')}
            </label>
            <label>
              <md-radio
                name="judgment"
                value="abnormal"
                ?checked=${judgment == 'abnormal'}
                aria-label=${i18next.t('label.judgment-abnormal')}
                disabled
              ></md-radio
              >${i18next.t('label.judgment-abnormal')}
            </label>
          </div>
        </label>

        <label>
          <h2><md-icon>build_circle</md-icon>&nbsp;<span>${i18next.t('label.corrective instruction')}</span></h2>
          <div instruction>${instruction}</div>
        </label>
      </form>
    `
  }

  onChangeInstruction(e: Event) {
    this.output ||= {}
    this.output.instruction = (e.target as HTMLTextAreaElement).value

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: this.output
      })
    )
  }

  updated(changes) {
    if (changes.has('input')) {
      this.fetchDataAssign()
    }
  }

  async fetchDataAssign() {
    const { dataSampleId, dataSample } = this.input || {}

    if (dataSample) {
      /* only for backwards compatibility - should be deleted later */
      this.dataSample = dataSample
    } else if (dataSampleId) {
      const response = await client.query({
        query: gql`
          query ($id: String!) {
            dataSample(id: $id) {
              id
              name
              description
              useCase
              key01
              key02
              key03
              key04
              key05
              data
              ooc
              oos
              dataSet {
                id
                name
                description
                monitorType
                monitorView
              }
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                subgroup
                type
                unit
                options
                quota
                spec
                stat
              }
              judgment
              workDate
              workShift
              updater {
                id
                name
              }
              updatedAt
              collectedAt
            }
          }
        `,
        variables: {
          id: dataSampleId
        }
      })

      this.dataSample = {
        ...response.data.dataSample
      }
    }
  }

  private showDataSampleView() {
    const dataSet = this.dataSample?.dataSet
    const monitorType = dataSet?.monitorType
    const monitorView = dataSet?.monitorView

    switch (monitorType) {
      case 'generated':
        return html`<ox-data-sample-view .dataSample=${this.dataSample}></ox-data-sample-view>`
      case 'checklist':
        return html`<ox-checklist-sample-view .dataSample=${this.dataSample}></ox-checklist-sample-view>`
      case 'board':
        break
      case 'page':
        break
      default:
        return html`<ox-data-sample-view .dataSample=${this.dataSample}></ox-data-sample-view>`
        break
    }
  }
}
